import {
	CurrencySettings,
	removeEscapeSlashes,
	UserCompany,
	ZomentumStorageKeys,
	CurrenciesForCustomSymbol,
	CurrenciesSymbol
} from "@/Contracts";
import cache from "@/Contracts/appCache";
import { InputNumberProps } from "antd/lib/input-number";

export const FallbackCurrency = "USD";
export const FallbackLocale = "en-US";

export const getCurrencySettings = (): { currency: string; locale: string } => {
	let currency = cache.get(ZomentumStorageKeys.Currency, false);
	let locale = cache.get(ZomentumStorageKeys.Locale, false);
	const userCompany: UserCompany = cache.get(
		ZomentumStorageKeys.UserCompany,
		true
	);

	currency =
		(currency && removeEscapeSlashes(currency)) ||
		userCompany?.settings?.currency ||
		FallbackCurrency;
	locale =
		(locale && removeEscapeSlashes(locale)) ||
		userCompany?.settings?.locale ||
		FallbackLocale;

	return {
		currency,
		locale
	};
};

/**
 *
 * @param price number that needed to be parsed
 * @param fallbackValue if the price provided is not a number, this will be returned instead
 * @param currencyOptions overrides passed to Intl.NumberFormat
 * @param currencySettings partial override for {currency, locale}; missing fields
 *        fall back to the company's settings. Callers in the document editor can
 *        pass just `{ currency: docCurrency }` to render in a non-company currency.
 */
export const currencyFormatter = (
	price: string | number | undefined | null,
	fallbackValue = "",
	currencyOptions?: Intl.NumberFormatOptions,
	currencySettings?: Partial<CurrencySettings>
) => {
	if (isNaN(Number(price))) {
		return fallbackValue || "--";
	}
	const companyDefaults = getCurrencySettings();
	const currency = currencySettings?.currency ?? companyDefaults.currency;
	const locale = currencySettings?.locale ?? companyDefaults.locale;
	try {
		if (CurrenciesForCustomSymbol.includes(currency)) {
			return `${Object(CurrenciesSymbol)[currency]}${Number(
				price
			).toLocaleString(locale, { minimumFractionDigits: 2 })}`;
		}

		return Number(price).toLocaleString(locale, {
			style: "currency",
			currencyDisplay: "symbol",
			currency,
			...currencyOptions
		});
	} catch (error) {
		console.error(error);
		return currency;
	}
};

/**
 * Pulls the currency code from an entity whose `.currency` may be either a
 * bare string code or a `{code, display_name}` object (depending on the
 * adapter path). Returns undefined when there is no entity-level currency so
 * `currencyFormatter` falls back to the company's primary currency.
 */
export const STRIPE_GATEWAY = "stripe";

export interface GatewayPaymentResolution {
	currency: string | undefined;
	amount: number;
}

export const resolveGatewayPaymentAmountAndCurrency = (input: {
	docCurrencyCode: string | undefined;
	companyCurrency: string | undefined;
	gateway: string | null | undefined;
	frozenOrLiveRate: number | null | undefined;
	rawAmount: number;
}): GatewayPaymentResolution => {
	const {
		docCurrencyCode,
		companyCurrency,
		gateway,
		frozenOrLiveRate,
		rawAmount
	} = input;
	const stripeNativelyHandlesCurrency =
		gateway?.toLowerCase() === STRIPE_GATEWAY;
	const docCurrencyDiffersFromPrimary =
		!!docCurrencyCode &&
		!!companyCurrency &&
		docCurrencyCode !== companyCurrency;
	const shouldConvertToPrimary =
		docCurrencyDiffersFromPrimary && !stripeNativelyHandlesCurrency;
	const rate =
		shouldConvertToPrimary &&
		frozenOrLiveRate !== null &&
		frozenOrLiveRate !== undefined &&
		frozenOrLiveRate > 0
			? frozenOrLiveRate
			: 1;
	const currency = shouldConvertToPrimary
		? companyCurrency
		: docCurrencyCode ?? companyCurrency;
	const amount = shouldConvertToPrimary ? rawAmount / rate : rawAmount;
	return { currency, amount };
};

const asGatewayString = (gateway: unknown): string | undefined =>
	typeof gateway === "string" && gateway.length > 0 ? gateway : undefined;

export const getEffectivePaymentCurrency = (
	document:
		| {
				currency_config?: { currency?: unknown } | null;
				company?:
					| { settings?: { currency?: string } }
					| null
					| undefined;
				payment?:
					| {
							gateway?: unknown;
							payment_options?: {
								credit_card?: { gateway?: unknown } | null;
								ach?: { gateway?: unknown } | null;
							} | null;
					  }
					| null;
		  }
		| null
		| undefined
): string | undefined => {
	const docCurrencyCode =
		getEntityCurrencyCode(document?.currency_config) || undefined;
	const companyCurrency = document?.company?.settings?.currency || undefined;
	const paymentOptions = document?.payment?.payment_options;
	const effectiveGateway =
		asGatewayString(document?.payment?.gateway) ??
		asGatewayString(paymentOptions?.credit_card?.gateway) ??
		asGatewayString(paymentOptions?.ach?.gateway);
	const isStripe = effectiveGateway?.toLowerCase() === STRIPE_GATEWAY;
	if (isStripe) {
		return docCurrencyCode ?? companyCurrency;
	}
	return companyCurrency ?? docCurrencyCode;
};

export interface PaymentSummaryInput {
	currency_config?: { currency?: unknown } | null;
	company?: { settings?: { currency?: string } } | null | undefined;
	payment?: {
		gateway?: unknown;
		status?: string;
		amount_collected?: number | null;
		amount_to_be_paid?: number | null;
	} | null;
}

export interface PaymentSummary {
	amount: number | null | undefined;
	currency: string | undefined;
}

export const getPaymentSummaryForDocument = (
	document: PaymentSummaryInput | null | undefined
): PaymentSummary => {
	const payment = document?.payment;
	const isPaid = payment?.status === "Paid";
	const amount = isPaid
		? payment?.amount_collected ?? payment?.amount_to_be_paid
		: payment?.amount_to_be_paid;
	return {
		amount,
		currency: getEffectivePaymentCurrency(document)
	};
};

export const isDocumentInNonPrimaryCurrency = (
	docCurrencyConfig: { currency?: unknown } | null | undefined,
	primaryCurrency: string | null | undefined
): boolean => {
	const docCurrencyCode = getEntityCurrencyCode(docCurrencyConfig);
	return (
		!!docCurrencyCode &&
		!!primaryCurrency &&
		docCurrencyCode !== primaryCurrency
	);
};

export const getEntityCurrencyCode = (
	entity: { currency?: unknown } | null | undefined
): string | undefined => {
	const c = entity?.currency;
	if (!c) return undefined;
	if (typeof c === "string") return c;
	if (typeof c === "object" && "code" in c) {
		return (c as { code?: string }).code || undefined;
	}
	return undefined;
};

export const percentageFormatter = (value = 0, divisor: number) => {
	if (isNaN(Number(value))) {
		return 0 + " %";
	}
	return (
		((value &&
			Math.round((divisor ? value / divisor : value) * 100 * 100) /
				100) ||
			0) + " %"
	);
};

export const getCurrencySymbol = (
	excludeSymbol?: boolean,
	excludeCurrency?: boolean
): string => {
	const { currency, locale } = getCurrencySettings();
	const symbol = new Intl.NumberFormat(locale, {
		style: "currency",
		currency: currency,
		minimumFractionDigits: 0,
		maximumFractionDigits: 0
	})
		.format(0)
		.replace(/\d/g, "")
		.trim();
	if (excludeCurrency) {
		return symbol;
	}
	if (excludeSymbol) {
		return currency;
	}
	if (excludeCurrency && excludeSymbol) {
		return "";
	}
	return `${currency}(${symbol})`;
};

export const customCurrencyFormatter = (
	price: number | string | undefined | null,
	currency?: string
) => {
	const { locale } = getCurrencySettings();
	try {
		return Number(price).toLocaleString(locale, {
			style: "currency",
			currencyDisplay: "symbol",
			currency: !!currency?.length ? currency : FallbackCurrency
		});
	} catch (error) {
		console.error(error);
		return currency;
	}
};

export const currencyInputProps: InputNumberProps = {
	formatter: currencyFormatter,
	min: 0,
	max: 99999999999
};

export interface RateImpact {
	subtotal: number;
	tax: number;
	total: number;
}

export const computeRateImpact = (
	grand: number,
	tax: number,
	frozenRate: number | null | undefined,
	liveRate: number
): RateImpact => {
	if (!frozenRate || frozenRate <= 0) {
		return { subtotal: 0, tax: 0, total: 0 };
	}
	const factor = liveRate / frozenRate - 1;
	const total = grand * factor;
	const taxImpact = tax * factor;
	return {
		subtotal: total - taxImpact,
		tax: taxImpact,
		total
	};
};
